import type {
  Content,
  CustomTableLayout,
  TableCell,
  TDocumentDefinitions,
} from 'pdfmake/interfaces.js';
import { formatDay } from '../../format.js';
import { TOKENS } from '../../tokens.js';
import type { Cell, CellKind, ReportTable } from './reports.js';

/** Bir dilin metin fonksiyonu (i18next `getFixedT`). */
export type ReportPdfT = (key: string, options?: Record<string, unknown>) => string;

export interface ReportPdfInput {
  title: string;
  /** Dönem satırı (ör. "10.09.2026 – 10.10.2026"). */
  period: string;
  /** Şirket markası (ADR 0019 §4). */
  company: string | null;
  /** Üretim zamanı, ISO-8601 UTC. */
  generatedAt: string;
  table: ReportTable;
  summary: { label: string; value: string }[];
  /** Sayı biçimi (arayüz dili). */
  locale: string;
}

const FONT = 'Roboto';

const LINES: CustomTableLayout = {
  hLineWidth: (i) => (i === 1 ? 0.8 : 0.4),
  vLineWidth: () => 0,
  hLineColor: () => TOKENS.border,
  paddingLeft: () => 3,
  paddingRight: () => 3,
  paddingTop: () => 2,
  paddingBottom: () => 2,
  fillColor: (row) => (row === 0 ? TOKENS['table-head-bg'] : null),
};

/** Hücre metni: gün `GG.AA.YYYY`, tutar iki ondalık, miktar en çok üç ondalık. */
export function formatCell(kind: CellKind, value: Cell, locale: string): string {
  if (value === null || value === '') return '—';
  if (kind === 'date' && typeof value === 'string') return formatDay(value);
  if (typeof value === 'number') {
    return kind === 'money'
      ? value.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : value.toLocaleString(locale, { maximumFractionDigits: 3 });
  }
  return String(value);
}

const numeric = (kind: CellKind) => kind === 'money' || kind === 'number';

/**
 * Raporun PDF'i (ADR 0040 #189; mimari belge "Teknoloji": raporlar pdfmake): A4 yatay, başlık,
 * şirket, dönem, tablo ve özet; altbilgide üretim zamanı ve sayfa. Saf fonksiyon.
 */
export function reportPdf(input: ReportPdfInput, t: ReportPdfT): TDocumentDefinitions {
  const { table } = input;
  const content: Content[] = [
    {
      columns: [
        {
          width: '*',
          stack: [
            { text: input.company ?? '', style: 'company' },
            { text: input.title, style: 'title' },
          ],
        },
        { width: 'auto', text: input.period, style: 'muted', alignment: 'right' },
      ],
    },
    {
      margin: [0, 12, 0, 0],
      table: {
        headerRows: 1,
        widths: table.columns.map((column) => (column.kind === 'text' ? '*' : 'auto')),
        body: [
          table.columns.map(
            (column): TableCell => ({
              text: column.label,
              style: 'th',
              alignment: numeric(column.kind) ? 'right' : 'left',
            }),
          ),
          ...table.rows.map((values) =>
            table.columns.map(
              (column, index): TableCell => ({
                text: formatCell(column.kind, values[index] ?? null, input.locale),
                alignment: numeric(column.kind) ? 'right' : 'left',
              }),
            ),
          ),
        ],
      },
      layout: LINES,
    },
  ];
  if (table.rows.length === 0) {
    content.push({ text: t('reports.view.empty'), style: 'muted', margin: [0, 8, 0, 0] });
  }
  if (input.summary.length > 0) {
    content.push({
      margin: [0, 12, 0, 0],
      table: {
        widths: ['auto', 'auto'],
        body: input.summary.map((item): TableCell[] => [
          { text: item.label, style: 'muted' },
          { text: item.value, bold: true, alignment: 'right' },
        ]),
      },
      layout: 'noBorders',
    });
  }
  return {
    pageSize: 'A4',
    pageOrientation: 'landscape',
    pageMargins: [32, 32, 32, 40],
    info: { title: input.title, creator: 'Felixmate' },
    defaultStyle: { font: FONT, fontSize: 8, color: TOKENS['navy-900'] },
    styles: {
      company: { fontSize: 11, bold: true },
      title: { fontSize: 15, bold: true, color: TOKENS['primary-600'] },
      muted: { color: TOKENS['text-secondary'] },
      th: { bold: true, color: TOKENS['text-secondary'] },
    },
    content,
    footer: (page, pages) => ({
      margin: [32, 10, 32, 0],
      columns: [
        {
          text: t('reports.pdf.footer', {
            at: `${input.generatedAt.slice(0, 16).replace('T', ' ')} UTC`,
          }),
          style: 'muted',
          fontSize: 7,
        },
        {
          text: t('reports.pdf.page', { page, pages }),
          alignment: 'right',
          style: 'muted',
          fontSize: 7,
        },
      ],
    }),
  };
}
